import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import SwipeCarousel from '../SwipeCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('') || '?';

const READING_QUOTES = [
  { text: 'A reader lives a thousand lives before he dies. The man who never reads lives only one.', author: 'George R.R. Martin' },
  { text: 'A book is a dream that you hold in your hand.', author: 'Neil Gaiman' },
  { text: 'There is no friend as loyal as a book.', author: 'Ernest Hemingway' },
  { text: 'Books are a uniquely portable magic.', author: 'Stephen King' },
  { text: 'Reading is essential for those who seek to rise above the ordinary.', author: 'Jim Rohn' },
  { text: 'I have always imagined that paradise will be a kind of library.', author: 'Jorge Luis Borges' },
  { text: 'Once you learn to read, you will be forever free.', author: 'Frederick Douglass' },
  { text: 'A book is a device to ignite the imagination.', author: 'Alan Bennett' },
  { text: 'Books permit us to voyage through time, to tap the wisdom of our ancestors.', author: 'Carl Sagan' },
  { text: 'Any book that helps a child to form a habit of reading, to make reading one of his deep and continuing needs, is good for him.', author: 'Maya Angelou' },
  { text: 'You can never get a cup of tea large enough or a book long enough to suit me.', author: 'C.S. Lewis' },
  { text: 'Reading is to the mind what exercise is to the body.', author: 'Joseph Addison' },
];

const FeaturedAuthorsCarousel = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pick one quote per page load — useMemo ensures it stays stable across
  // re-renders within the same visit (so the quote doesn't flicker as the
  // carousel auto-advances).
  const quote = React.useMemo(
    () => READING_QUOTES[Math.floor(Math.random() * READING_QUOTES.length)],
    [],
  );
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/featured-authors`)
      .then((res) => {
        if (mounted) setAuthors(res.data?.authors || []);
      })
      .catch(() => {
        if (mounted) setAuthors([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  if (loading || authors.length === 0) return null;

  const renderItem = (a, idx = 0) => (
    <button
      type="button"
      onClick={() => a.primary_book_id && navigate(`/book/${a.primary_book_id}`)}
      className="w-full block text-left bg-white/5 backdrop-blur-sm border border-white/10 hover:border-fuchsia-400/40 transition-colors rounded-2xl overflow-hidden group animate-fade-in"
      style={{ animationDelay: `${idx * 100}ms`, animationFillMode: 'backwards' }}
      data-testid={`featured-author-card-${a.name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-stretch">
        <div className="w-32 sm:w-40 aspect-[2/3] bg-zinc-900 overflow-hidden flex-shrink-0">
          {a.primary_book_cover ? (
            <img
              src={a.primary_book_cover}
              alt={a.primary_book_title || ''}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-fuchsia-300">
              {initials(a.name)}
            </div>
          )}
        </div>
        <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center">
          <p className="text-fuchsia-300 text-xs uppercase tracking-[0.2em] mb-2">Featured author</p>
          <h4 className="text-xl sm:text-2xl font-bold text-white mb-2 leading-tight">{a.name}</h4>
          {a.tagline && <p className="text-sm text-gray-300 mb-3">{a.tagline}</p>}
          {a.primary_book_title && (
            <p className="inline-flex items-center gap-1.5 text-sm text-gray-400 group-hover:text-fuchsia-200 transition-colors">
              <BookOpen className="h-3.5 w-3.5" />
              <span className="line-clamp-1">{a.primary_book_title}</span>
            </p>
          )}
        </div>
      </div>
    </button>
  );

  return (
    <section className="mb-12 sm:mb-16" data-testid="featured-authors-section">
      <div className="mb-6">
        <p className="text-fuchsia-400 text-xs uppercase tracking-[0.25em] font-semibold mb-2">In the spotlight</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
          Meet the voices behind the books
        </h2>
        <blockquote
          key={quote.text}
          className="mt-4 max-w-2xl border-l-2 border-fuchsia-500/60 pl-4 italic text-base sm:text-lg text-gray-300 leading-relaxed animate-fade-in"
          data-testid="featured-authors-quote"
        >
          &ldquo;{quote.text}&rdquo;
          <span className="block not-italic text-xs text-gray-500 mt-1.5" data-testid="featured-authors-quote-author">
            — {quote.author}
          </span>
        </blockquote>
      </div>
      <SwipeCarousel
        items={authors}
        testId="featured-authors"
        ariaLabel="Featured authors carousel"
        renderItem={renderItem}
      />
    </section>
  );
};

export default FeaturedAuthorsCarousel;
